/**
 * Backend API ORIGIN — no `/api` segment, no trailing slash.
 *
 * `apiClient.js` builds every request as `${API_URL}/api/<path>`, so this must
 * be the origin only. It is normalised below so `.env` may carry EITHER shape
 * (`https://host` or `https://host/api/`) without breaking the client — the
 * website repo's REACT_APP_URL includes `/api/`, and both repos are now
 * configured from the same value.
 *
 * Was hard-coded to the staging host until 2026-09-28; `REACT_APP_URL` was read
 * into `API_URL_ENV` but nothing consumed it, so `.env` could not actually move
 * the CMS off staging. It can now. The literal remains as the fallback for a
 * build with no env file.
 */
const normaliseApiOrigin = (value) =>
  (value || "")
    .trim()
    .replace(/\/+$/, "")
    .replace(/\/api$/i, "");

export const API_URL =
  normaliseApiOrigin(process.env.REACT_APP_URL) || "https://dpaapis.ctsdemo.ae";
//export const API_URL = "//dpabewebsite.azurewebsites.net";

/** @deprecated Raw, un-normalised env value. Use `API_URL`. */
export const API_URL_ENV = process.env.REACT_APP_URL;

/**
 * Origin of the PUBLIC WEBSITE (DPA-FE) — not the API.
 *
 * Needed by exactly one thing: the navigation icon picker, which previews
 * icons the website serves from its own `public/` folder. A menu item stores
 * the icon as a ROOT-RELATIVE PATH (`/assets/svg/header/search.svg`) because
 * that is what the website resolves — so the CMS has to supply the origin
 * itself to render a preview.
 *
 * Left as an empty string when unset rather than guessing: the picker then
 * says the variable is missing instead of drawing 387 broken images.
 */
const normaliseOrigin = (value) => (value || '').trim().replace(/\/+$/, '');

/**
 * RESOLUTION ORDER, and the first one matters most:
 *
 *   1. `window.__DPA_CONFIG__.websiteUrl` — `public/env-config.js`, edited on
 *      the SERVER after deployment.
 *   2. `REACT_APP_WEBSITE_URL` — compiled into the bundle at build time.
 *   3. `''` — unset.
 *
 * Runtime wins because a build is made once and deployed to several
 * environments. Relying on (2) alone is what sent the announcement preview to
 * `http://localhost:3000` from the testing server: the value was correct for
 * the machine that produced the build and wrong everywhere it was then copied.
 *
 * Still an empty string when nothing is set, rather than a guess — callers
 * report a missing configuration instead of opening a dead tab or drawing 387
 * broken images.
 */
export const WEBSITE_URL =
  normaliseOrigin(
    typeof window !== 'undefined' ? window.__DPA_CONFIG__?.websiteUrl : ''
  ) || normaliseOrigin(process.env.REACT_APP_WEBSITE_URL);

/**
 * `true` when the resolved website origin points at a developer's own machine.
 *
 * Correct locally and definitely wrong on any deployed server, so it is worth
 * detecting rather than opening a tab the operator cannot reach: `localhost`
 * on the testing server resolves to the TESTING SERVER itself, which answers
 * with something unrelated or nothing at all.
 */
export const isLocalOrigin = (value) =>
  /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(?::\d+)?$/i.test(value || '');

export const quillModules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
  ],
  clipboard: {
    matchVisual: false,
  },
};

export const quillFormats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

/**
 * Session helpers. One place that knows where the session lives, so clearing it
 * cannot be done by halves - the previous code removed `token` and left
 * `tokenExpiry` and `userId` behind, which made the app look signed-in to some
 * screens and signed-out to others.
 */
export const SESSION_KEYS = [
  "token",
  "tokenExpiry",
  "refreshToken",
  "refreshTokenExpiry",
  "userId",
  "cmsPages",
  "rolePages",
  // The idle clock (services/activity.js). It MUST be cleared with the rest:
  // left behind, an hour-old timestamp would survive sign-out, and the guard
  // would expire the next session on its first tick - a sign-in loop with a
  // correct password.
  "lastActivityAt",
];

export const getAuthToken = () => localStorage.getItem("token");
export const getRefreshToken = () => localStorage.getItem("refreshToken");

/**
 * The sidebar tree, as returned by `POST /api/Auth/Login` -> `cmsPages`.
 *
 * Persisted because `GET /api/Auth/GetPageAccess` - which the sidebar used to
 * be built from - returns `nested: []` on EVERY row, while the login response
 * returns the full tree for the same user at the same moment (verified
 * 2026-09-22). So the sidebar rendered 14 flat rows, three of which
 * (`طلبات`, `المركز الإعلامي`, `المجلات والأبحاث`) carry an empty `path` and
 * went nowhere.
 *
 * Reported to the backend as ask D in
 * DPA-FE `docs/Controlled/MainMenu/09-backend-request.md`. Once GetPageAccess
 * serves the tree this becomes a redundant fast path rather than a workaround,
 * and `SideBar.jsx` will still prefer whichever source actually has children.
 *
 * Returns `null` rather than throwing on malformed JSON: a corrupt cache must
 * degrade to "fetch it from the API", never to a blank admin shell.
 */
export const getStoredCmsPages = () => {
  try {
    const raw = localStorage.getItem("cmsPages");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch (_error) {
    localStorage.removeItem("cmsPages");
    return null;
  }
};

/**
 * The MASTER list of assignable pages, as returned by
 * `GET /api/RolePage/GetRolePages`.
 *
 * Cached for one reason: it decides which screens the route gate is allowed to
 * ENFORCE (`services/pageAccess.js` -> `isGateable`). Without a cache, a single
 * failed request would leave that list empty, and an empty list means "nothing
 * is enforceable" - every screen silently open again. A stale cache is a far
 * better answer than no answer, because the list changes about once a release.
 *
 * Same contract as `getStoredCmsPages`: `null` on anything unusable, never a
 * throw, and a corrupt entry removes itself.
 */
export const getStoredRolePages = () => {
  try {
    const raw = localStorage.getItem("rolePages");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch (_error) {
    localStorage.removeItem("rolePages");
    return null;
  }
};

export const setStoredRolePages = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return;
  try {
    localStorage.setItem("rolePages", JSON.stringify(rows));
  } catch (_error) {
    // Quota, or a serialisation failure. Losing the cache costs one request on
    // the next load; failing the session over it would not be a trade.
  }
};

export const clearSession = () => {
  SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
};

export const securityHeaders = {
  /**
   * A GETTER, NOT A VALUE - and this matters.
   *
   * This was `Authorization: \`Bearer ${localStorage.getItem("token")}\``, a
   * plain property evaluated ONCE when this module is first imported. That is
   * before the user has signed in, so every request for the rest of that page's
   * life carried `Bearer null`; and after a token is replaced, requests kept
   * sending the old one. A getter is read at REQUEST time instead, so the header
   * always carries whatever token is current.
   *
   * Deliberately a getter rather than turning `securityHeaders` into a function:
   * it is imported in 64 files across 258 call sites, all of which spread or
   * pass it as an object. Changing the shape would mean touching every one of
   * them; this fixes the defect without a single call-site change.
   */
  get Authorization() {
    return `Bearer ${getAuthToken() ?? ""}`;
  },

  // Content Security Policy (CSP) - Controls the resources the browser is allowed to load
  "Content-Security-Policy":
    "default-src 'self'; script-src 'self' ; object-src 'none'; frame-ancestors 'none';",

  // X-Frame-Options - Prevents the site from being embedded in iframes
  "X-Frame-Options": "DENY",

  // X-Content-Type-Options - Prevents MIME type sniffing
  "X-Content-Type-Options": "nosniff",

  // Referrer-Policy - Controls how much referrer information should be included with requests
  "Referrer-Policy": "no-referrer",

  // Permissions-Policy - Controls which features and APIs can be used in the browser
  "Permissions-Policy":
    "geolocation=(self), microphone=(), camera=(), autoplay=()",

  // Strict-Transport-Security (HSTS) - Enforces secure (HTTPS) connections to the server
  "Strict-Transport-Security": "max-age=31536000; includeSubDomains; preload",

  // X-XSS-Protection - Enables the cross-site scripting filter built into most browsers
  "X-XSS-Protection": "1; mode=block",

  // Expect-CT - Ensures that any misissued certificates are quickly discovered
  "Expect-CT": "max-age=86400, enforce,",

  // Cache-Control - Controls how documents are cached, stored, and revalidated by browsers
  "Cache-Control": "no-store, no-cache, must-revalidate, proxy-revalidate",
};
